import { useAuth } from "@/context/AuthContext";
import roles from "@/assets/roles.json";
import { User, Camera } from "lucide-react";
import { useState, useRef } from "react";
import { setSessionItem } from "@/utils/storage";

export default function UserMetaCard() {
  const { user, token, setUser } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setIsUploading(true);
    try {
      // 1. Guardar la foto vieja para borrarla despues
      const oldPhoto = user?.foto;

      // 2. Subir el archivo al endpoint general de archivos
      const uploadResponse = await fetch('files/upload', {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        const photoUrl = `/files/image/${uploadData.file}`;

        // 3. Actualizar el usuario con la nueva URL de la foto
        const updateResponse = await fetch(
          `usuarios/update/${user.id}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ foto: photoUrl }),
          }
        );

        if (updateResponse.ok) {
          const updatedUserData = await updateResponse.json();
          
          // IMPORTANTE: Mezclar con el usuario actual para no perder propiedades de sesion como isMobileApp
          const newUserState = { ...user, ...updatedUserData };
          setUser(newUserState);
          setSessionItem("user", JSON.stringify(newUserState));

          // 4. Borrar la foto vieja del servidor si existia
          if (oldPhoto && oldPhoto.includes("files/image/")) {
            const oldFileName = oldPhoto.split("/").pop();
            if (oldFileName) {
              await fetch('/files/delete', {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ filePath: `img/${oldFileName}` }),
              });
            }
          }
        } else {
          console.error("Error al actualizar la foto del usuario");
        }
      } else {
        console.error("Error al subir el archivo");
      }
    } catch (error) {
      console.error("Error de red:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const getImageUrl = (path: string) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${path}`;
  };

  return (
    <>
      <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
            <div className="relative group">
              <div
                onClick={handleImageClick}
                className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800 cursor-pointer relative"
              >
                {user?.foto ? (
                  <img
                    src={getImageUrl(user.foto) || ""}
                    alt="user"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-full h-full text-gray-400" />
                )}

                {/* Overlay de carga o edicion */}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {isUploading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : (
                    <Camera className="text-white w-6 h-6" />
                  )}
                </div>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
            <div className="order-3 xl:order-2">
              <h4 className="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                {user?.nombres} {user?.apellidos}
              </h4>
              <div className="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {roles.find((role) => role.id === user?.rol)?.name}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
